import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styles from './ChatConsoleView.module.scss';
import ChatConsoleSidebar from 'component/molecules/chat-console-sidebar/ChatConsoleSidebar';
import { useBrandingContext } from 'app-context/BrandingsContext';
import { useUserContext } from 'app-context/user/UserContext';
import { usePost } from 'hooks/usePost';
import { chat, companyEndpoints } from 'hooks/endpoints';
import { useFetch } from 'hooks/useFetch';
import {
  createUserMessage,
  transformChatData,
  transformNewChatData,
} from './factory/transformData';
import ChatBody from './components/chat-body/ChatBody';
import toast from 'react-hot-toast';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Header from 'component/layout/header/Header';
import Button from 'component/atom/forms/button/Button';
import axios from 'axios';
import { API_URL } from 'constants';
import { securityHeaders } from 'helpers/headers';
import { useAutoUpdate } from 'app-context/auto-update/AutoUpdate';
import ChatHeader from './components/chat-header/ChatHeader';
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from './components/resizable-panel/Resizable';

const ChatConsoleView = ({
  isOpen,
  setIsOpen,
  hasSidebar = true,
  className,
  bodyClassName,
  chattingClassName,
  bot,
  inputContainerClass,
  isWorkFlow,
  isExpanded,
  triggerNewConversation,
  hasBotId,
  disableConsole,
  externalConversationId,
  hasHeader = true,
  isWorkFlowChat = false,
}) => {
  const hasFetched = useRef(false);
  const navigate = useNavigate();

  // this id is used to fetch the chat data from the api (if available means we are in the conversation page)
  const { isActive } = useAutoUpdate();

  const { id } = useParams();
  const location = useLocation();
  const ExternalChatId = location.state?.chatId || externalConversationId;
  const conversationId = id;
  const { userData } = useUserContext();
  const { brandingData } = useBrandingContext();
  const [suggestedQuestions, setSuggestedQuestions] = useState([]);
  const [idleTime, setIdleTime] = useState(0);
  const [guestChat, setGuestChat] = useState({ messages: [] });
  const [isChatting, setIsChatting] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [isFetchingChat, setIsFetchingChat] = useState(true);
  const [capture, setCapture] = useState();
  const [chatId, setChatId] = useState(() => {
    return ExternalChatId || '';
  });
  const [forceResetForm, setForceResetForm] = useState(false);
  const [restartForm, setRestartForm] = useState(false);
  const workflowClosed = isWorkFlowChat && !isExpanded;

  const { mutate: postResponse, isPending: isRequesting } = usePost();
  const { mutate: submitChat, isPending: isSubmitChatLoading } = usePost();
  const { data, isFetching, refetch } = useFetch(
    ['customMessageData'],
    companyEndpoints.getChatSettings,
    { enabled: true },
    { botId: hasBotId ? conversationId : bot }
  );

  const {
    data: chatsData,
    refetch: refetchChatsList,
    isLoading,
    isFetching: isFetchingChatsList,
  } = useFetch(
    ['chat-list', hasBotId ? conversationId : bot],
    chat.getChats(hasBotId ? conversationId : bot),
    {
      enabled: hasSidebar && !disableConsole ? true : false,
    }
  );

  const { data: singleChatData, refetch: refetchSingleChat } = useFetch(
    ['single-chat', hasBotId ? ExternalChatId : chatId],
    chat.getChatById(hasBotId ? ExternalChatId : chatId),
    {
      enabled: false,
    }
  );

  const filteredChats = useMemo(() => {
    return chatsData?.data?.filter((chat) =>
      chat.chatTitle.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [searchQuery, chatsData]);

  const fetchChat = async () => {
    if (workflowClosed) return;
    setIsFetchingChat(true);
    try {
      const chatToFetch = ExternalChatId || chatId;
      if (
        !chatToFetch ||
        chatToFetch === '00000000-0000-0000-0000-000000000000'
      ) {
        setIsFetchingChat(false);
        return;
      }

      await refetchSingleChat().then((res) => {
        setIsFetchingChat(false);
        if (
          res?.data?.data?.chatUpdatedDetails &&
          Object.keys(res.data.data.chatUpdatedDetails).length > 0
        ) {
          const chatData = transformNewChatData(
            res.data.data.chatUpdatedDetails
          );

          setGuestChat((prev) => ({
            ...chatData,
            chatID: prev.chatID || chatData.chatID,
          }));
        }
      });
    } catch (error) {
      setIsFetchingChat(false);
      console.error('Error fetching chat:', error);
    }
  };
  const handleIdleSubmit = () => {
    const formData = new FormData();
    formData.append('ChatId', guestChat.chatID);
    formData.append('BotId', hasBotId ? conversationId : bot);
    formData.append('IsIdle', true);
    if (isWorkFlow) formData.append('IsTryMe', true);

    submitChat(
      { data: formData, url: chat.postResponse, isFormData: true },
      {
        onSuccess: (res) => {
          if (res.data.suggestedQuestions) {
            setSuggestedQuestions(res.data.suggestedQuestions);
          }
          setGuestChat((prev) => ({
            ...prev,
            messages: [
              ...prev.messages,
              ...transformChatData(res.data).messages,
            ],
          }));
        },
        onError: () => {
          toast.error('Failed to send idle message.');
        },
      }
    );
  };
  const startNewBotConversation = () => {
    if (workflowClosed) return;
    const botId = hasBotId ? conversationId : bot;
    if (!botId) {
      return;
    }
    setIsFetchingChat(true);
    const formData = new FormData();
    const newChatId = '00000000-0000-0000-0000-000000000000';
    setChatId(newChatId);
    formData.append('ChatId', newChatId);

    formData.append('BotId', hasBotId ? conversationId : bot);
    if (isWorkFlow) formData.append('IsTryMe', true);
    postResponse(
      { data: formData, url: chat.postResponse, isFormData: true },
      {
        onSuccess: (res) => {
          setIdleTime(res?.data.idleTime);

          setCapture(res?.data.capture);
          setGuestChat(transformChatData(res.data));
          if (res.data.suggestedQuestions) {
            setSuggestedQuestions(res.data.suggestedQuestions);
          } else {
            setSuggestedQuestions([]);
          }
          setIsFetchingChat(false);
        },
        onError: () => {},
      }
    );
  };

  const appendError = () => {
    setGuestChat((prev) => {
      const updatedMessages = [...prev.messages];

      if (updatedMessages.length > 0) {
        const lastMessage = updatedMessages[updatedMessages.length - 1];

        const updatedContent = lastMessage.content.flatMap((item) => ({
          ...item,
          isError: true,
        }));

        updatedMessages[updatedMessages.length - 1] = {
          ...lastMessage,
          content: updatedContent,
        };
      }
      return { ...prev, messages: updatedMessages };
    });
  };
  const handleSubmit = useCallback(
    async (values) => {
      try {
        if (
          !values.Question?.trim() &&
          (!values.images || values.images.length === 0) &&
          (!values.files || values.files.length === 0) &&
          !values.audio
        ) {
          return;
        }

        const loadingMessage = {
          role: 'bot',
          content: [{ type: 'loading', value: '...' }],
          isLoading: true,
        };

        setForceResetForm(!forceResetForm);
        setIsChatting(true);

        const ZERO_ID = '00000000-0000-0000-0000-000000000000';

        const effectiveChatId =
          chatId && chatId !== ZERO_ID ? chatId : guestChat?.chatID;

        const formData = new FormData();
        formData.append('ChatId', effectiveChatId);
        formData.append('BotId', hasBotId ? conversationId : bot);
        if (isWorkFlow) formData.append('IsTryMe', true);
        formData.append('Question', values.Question?.trim() || '');

        if (values.sourceHandle) {
          formData.append('SourceHandle', values.sourceHandle);
        }

        values.images?.forEach((img) =>
          formData.append('Attachments', img.file)
        );
        values.files?.forEach((file) =>
          formData.append('Attachments', file.file)
        );
        if (values.audio && values.audio.file) {
          formData.append('Attachments', values.audio.file);
        }

        const userMessage = createUserMessage(values);

        setGuestChat((prev) => ({
          ...prev,
          messages: [
            ...prev.messages,
            { ...userMessage, workflowName: '' },
            loadingMessage,
          ],
        }));
        submitChat(
          { data: formData, url: chat.postResponse, isFormData: true },
          {
            onSuccess: (res) => {
              setGuestChat((prev) => {
                const messages = [...prev.messages];
                const lastUserIndex = messages.findIndex((msg, index) => {
                  return (
                    msg.role === 'user' &&
                    index === messages.findLastIndex((m) => m.role === 'user')
                  );
                });

                if (lastUserIndex !== -1) {
                  messages[lastUserIndex] = {
                    ...messages[lastUserIndex],
                    workflowName: res.data.workflowName || '',
                  };
                }

                const updatedMessages = [
                  ...messages.filter((msg) => !msg.isLoading),
                  ...transformChatData(res.data).messages,
                ];

                return {
                  ...prev,
                  messages: updatedMessages,
                };
              });

              if (res.data.suggestedQuestions) {
                setSuggestedQuestions(res.data.suggestedQuestions);
              } else {
                setSuggestedQuestions([]);
              }
              if (hasSidebar) refetchChatsList();
            },
            onError: () => {
              appendError();
              setGuestChat((prev) => ({
                ...prev,
                messages: prev.messages.filter((msg) => !msg.isLoading),
              }));
              // setSuggestedQuestions([]);
            },
          }
        );
      } catch (error) {
        toast.error('Unexpected error, please try again later');
      }
    },
    [guestChat, bot, conversationId]
  );

  useEffect(() => {
    if (workflowClosed) return;
    const activeChatId = ExternalChatId || chatId;

    if (hasSidebar) {
      if (
        activeChatId &&
        activeChatId !== '00000000-0000-0000-0000-000000000000'
      ) {
        fetchChat();
      } else {
        startNewBotConversation();
      }
    } else if (isExpanded && !hasFetched.current) {
      if (
        activeChatId &&
        activeChatId !== '00000000-0000-0000-0000-000000000000'
      ) {
        fetchChat();
      } else {
        startNewBotConversation();
      }
      hasFetched.current = true;
    }
  }, [chatId, isExpanded, ExternalChatId]);

  useEffect(() => {
    if (workflowClosed) return;
    if (chatId || ExternalChatId) {
      refetchSingleChat();
    }
  }, [chatId, ExternalChatId, isExpanded]);

  useEffect(() => {
    if (workflowClosed) return;
    if (triggerNewConversation) {
      startNewBotConversation();
      setRestartForm(!restartForm);
    }
  }, [triggerNewConversation, isExpanded]);

  useEffect(() => {
    refetch();
  }, [isActive]);

  useEffect(() => {
    refetchChatsList();
    if (location.pathname == '/chat-console') {
      if (!workflowClosed) {
        startNewBotConversation();
      }
    }
  }, [bot, isExpanded]);

  const handleExport = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/Chat/DownloadChatConversations/${ExternalChatId}`,
        {
          responseType: 'blob',
          headers: securityHeaders(),
        }
      );
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');

      link.href = url;
      link.setAttribute('download', 'ChatConversation.pdf');
      document.body.appendChild(link);
      link.click();

      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export failed', error);
    }
  };
  const chatBodyProps = {
    isOpen,
    isFetchingChat,
    isChatting,
    isWorkFlow,
    guestChat,
    userData,
    isRequesting,
    hasSidebar,
    chatId: guestChat?.chatID,
    bot: hasBotId ? conversationId : bot,
    isSubmitChatLoading,
    capture,
    setCapture,
    handleSubmit,
    className,
    chattingClassName,
    inputContainerClass,
    bodyClassName,
    forceResetForm,
    setGuestChat,
    setSuggestedQuestions,
    suggestedQuestions,
    botSettingsData: data,
    isExpanded,
    restartForm,
    disableConsole,
    isConversation: ExternalChatId,
    idleTime,
    handleIdleSubmit,
    triggerNewConversation,
  };
  return (
    <div
      className={`${styles['chat-console']} ${
        isWorkFlow ? styles['workflow-layout'] : ''
      }`}
    >
      {ExternalChatId && hasHeader ? (
        <Button
          onClick={handleExport}
          className={styles['export-btn']}
          label='Export'
        />
      ) : null}
      {disableConsole && hasHeader && (
        <Header
          title='Conversation'
          isBack={true}
          containerClassname={styles['absolute-header']}
          onBackClick={() => navigate(-1)}
          isConversation={ExternalChatId}
        />
      )}

      {!isWorkFlow && !disableConsole ? (
        <ResizablePanelGroup direction='horizontal'>
          <ResizablePanel defaultSize={20}>
            {hasSidebar && !disableConsole && (
              <ChatConsoleSidebar
                isOpen={isOpen}
                setIsOpen={setIsOpen}
                brandingData={brandingData}
                botId={hasBotId ? conversationId : bot}
                getActiveChat={(id) => {
                  if (id !== '00000000-0000-0000-0000-000000000000') {
                    setChatId(id);
                    setForceResetForm((v) => !v);
                  }
                }}
                ChatId={guestChat?.chatID}
                chatsData={filteredChats}
                onNewChatClick={startNewBotConversation}
                onSearch={setSearchQuery}
                refetchChatsList={refetchChatsList}
                isLoading={isFetchingChatsList || isLoading}
              />
            )}
          </ResizablePanel>
          <ResizableHandle withHandle />
          <ResizablePanel>
            <ChatBody {...chatBodyProps} disableConsole={disableConsole} />
          </ResizablePanel>
        </ResizablePanelGroup>
      ) : (
        <ChatBody {...chatBodyProps} disableConsole={disableConsole} />
      )}
    </div>
  );
};

export default ChatConsoleView;
